/**
 * 工作区成员表格列定义
 * 提供可配置和可扩展的表格列定义
 */

import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from '@you/ui'
import type { WorkspaceMemberVO } from '@/api/types/workspace.types'
import { memberPermissions } from '@/hooks/useWorkspaceMemberActions'

/**
 * 成员头像组件（包含姓名、角色标签等信息）
 */
export const MemberAvatar = ({ member }: { member: WorkspaceMemberVO }) => {
  const isCurrentUser = memberPermissions.isCurrentUser(member)
  const isOwner = memberPermissions.isOwner(member)

  return (
    <div className="flex items-center gap-3 justify-start text-left">
      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
        {member.avatar ? (
          <img
            src={member.avatar}
            alt={member.userName}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-sm font-medium text-gray-600">
            {member.userName?.charAt(0)?.toUpperCase()}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            style={{
              color: '#8047FF',
              fontSize: '16px',
              fontWeight: 400,
            }}
          >
            {member.userName}
          </span>
          {isCurrentUser && (
            <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-small bg-green-100 text-green-800 rounded">
              我
            </span>
          )}
          {isOwner && (
            <span
              className="inline-flex items-center px-2 py-1 text-xs font-small rounded-full"
              style={{ background: '#EFF7FF', color: '#1E53FF' }}
            >
              所有者
            </span>
          )}
        </div>
        {member.phone && (
          <span
            style={{
              color: '#8047FF',
              fontSize: '16px',
              fontWeight: 400,
            }}
          >
            {member.phone}
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * 成员操作按钮组件
 */
export const MemberActions = ({
  member,
  onRemove,
  isRemoving,
  disabled = false,
}: {
  member: WorkspaceMemberVO
  onRemove: (member: WorkspaceMemberVO) => void
  isRemoving: boolean
  disabled?: boolean
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const canRemove = memberPermissions.canRemoveMember(member)

  const handleRemoveClick = () => {
    setShowConfirmDialog(true)
  }

  const handleConfirmRemove = () => {
    onRemove(member)
    setShowConfirmDialog(false)
  }

  if (!canRemove) {
    return <span className="text-sm text-gray-400">-</span>
  }

  return (
    <>
      <button
        onClick={handleRemoveClick}
        disabled={isRemoving || disabled}
        className="text-orange-500 hover:text-orange-600 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
        style={{ color: '#FF9452', fontSize: '16px' }}
      >
        {isRemoving ? '移除中...' : '移除'}
      </button>

      <ConfirmRemoveDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        member={member}
        onConfirm={handleConfirmRemove}
        isRemoving={isRemoving}
      />
    </>
  )
}

/**
 * 成员表格列配置选项
 */
export interface MemberTableColumnOptions {
  /** 是否显示头像列 */
  showAvatar?: boolean
  /** 是否显示角色列 */
  showRole?: boolean
  /** 是否显示加入时间列 */
  showJoinTime?: boolean
  /** 是否显示操作列 */
  showActions?: boolean
  /** 操作列是否禁用 */
  actionsDisabled?: boolean
  /** 自定义列 */
  customColumns?: ColumnDef<WorkspaceMemberVO>[]
}

/**
 * 创建工作区成员表格列定义
 * @param onRemoveMember 移除成员回调
 * @param isRemoving 是否正在移除
 * @param options 列配置选项
 */
export const createMemberTableColumns = (
  onRemoveMember: (member: WorkspaceMemberVO) => void,
  isRemoving: boolean,
  options: MemberTableColumnOptions = {}
): ColumnDef<WorkspaceMemberVO>[] => {
  const {
    showAvatar = true,
    showRole = true,
    showJoinTime = true,
    showActions = true,
    actionsDisabled = false,
    customColumns = [],
  } = options

  const columns: ColumnDef<WorkspaceMemberVO>[] = []

  // 成员信息列（头像+姓名）
  if (showAvatar) {
    columns.push({
      accessorKey: 'userName',
      header: '成员信息',
      cell: ({ row }) => <MemberAvatar member={row.original} />,
    })
  }

  // 角色列（已移至成员信息列中）
  if (showRole) {
    columns.push({
      accessorKey: 'isOwner',
      header: '角色',
      cell: ({ row }) => {
        const isOwner = memberPermissions.isOwner(row.original)
        return isOwner ? '所有者' : '成员'
      },
    })
  }

  // 加入时间列
  if (showJoinTime) {
    columns.push({
      accessorKey: 'createTime',
      header: '加入时间',
      cell: ({ row }) => {
        const timestamp = row.original.createTime
        if (!timestamp) return '-'
        // 如果是时间戳（数字），转换为日期
        const date =
          typeof timestamp === 'number'
            ? new Date(timestamp)
            : new Date(timestamp)

        // 格式化为 YYYY/MM/DD HH:mm:ss
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        const seconds = String(date.getSeconds()).padStart(2, '0')

        return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`
      },
    })
  }

  // 自定义列
  if (customColumns.length > 0) {
    columns.push(...customColumns)
  }

  // 操作列
  if (showActions) {
    columns.push({
      id: 'actions',
      header: '操作',
      cell: ({ row }) => (
        <MemberActions
          member={row.original}
          onRemove={onRemoveMember}
          isRemoving={isRemoving}
          disabled={actionsDisabled}
        />
      ),
    })
  }

  return columns
}

/**
 * 预定义的列配置模板
 */
export const memberTablePresets = {
  /** 完整显示（默认） */
  full: {
    showAvatar: true,
    showRole: false,
    showJoinTime: true,
    showActions: true,
  },

  /** 只读模式（不显示操作列） */
  readonly: {
    showAvatar: true,
    showRole: false,
    showJoinTime: true,
    showActions: false,
  },

  /** 简化模式（只显示基本信息） */
  simple: {
    showAvatar: true,
    showRole: false,
    showJoinTime: false,
    showActions: true,
  },

  /** 详细模式（包含所有信息） */
  detailed: {
    showAvatar: true,
    showRole: false,
    showJoinTime: true,
    showActions: true,
    customColumns: [
      {
        accessorKey: 'phone',
        header: '手机号',
        cell: ({ row }: { row: { original: WorkspaceMemberVO } }) => {
          return row.original.phone || '-'
        },
      },
    ] as ColumnDef<WorkspaceMemberVO>[],
  },
} as const

/**
 * 确认删除Dialog组件
 */
const ConfirmRemoveDialog = ({
  open,
  onOpenChange,
  member,
  onConfirm,
  isRemoving,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  member: WorkspaceMemberVO | null
  onConfirm: () => void
  isRemoving: boolean
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader title="移除成员" />
        <div className="px-6 py-4">
          <p className="text-gray-700">
            确定要移除成员{' '}
            <span className="font-medium text-gray-900">
              "{member?.userName}"
            </span>{' '}
            吗？
          </p>
          <p className="text-sm text-gray-500 mt-2">此操作不可撤销。</p>
        </div>
        <DialogFooter>
          <Button
            onClick={() => onOpenChange(false)}
            variant="outline"
            disabled={isRemoving}
          >
            取消
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isRemoving}
            className="bg-red-600 hover:bg-red-700"
          >
            {isRemoving ? '移除中...' : '确认移除'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
